import { Minus, Plus, Trash2 } from 'lucide-react';
import type { CartItem } from '@/domains/mobile-web/types/cart.types';
import { authorLabel, categoryLabel } from '@/domains/mobile-web/utils/bundle';
import { formatPrice } from '@/domains/mobile-web/utils/price';
import { cn } from '@/lib/utils';

interface CardCartItemProps {
  product: CartItem;
  checked?: boolean;
  minQuantity?: number;
  maxQuantity?: number;
  onCheckedChange?: (checked: boolean, product: CartItem) => void;
  onQuantityChange?: (quantity: number, product: CartItem) => void;
  onRemove?: (product: CartItem) => void;
  className?: string;
  showAddProduct?: boolean;
  isLast?: boolean;
  showSubtotal?: boolean;
}

const CardCartItem = ({
  product,
  checked = false,
  minQuantity = 1,
  maxQuantity,
  onCheckedChange,
  onQuantityChange,
  showAddProduct = true,
  onRemove,
  isLast = false,
  showSubtotal = false,
  className = '',
}: CardCartItemProps) => {
  const { quantity } = product;
  const checkboxId = `cart-item-${product.id}`;
  const canDecrease = quantity > minQuantity;
  const canIncrease = maxQuantity === undefined || quantity < maxQuantity;

  const handleDecrease = () => {
    if (!canDecrease) return;
    onQuantityChange?.(quantity - 1, product);
  };

  const handleIncrease = () => {
    if (!canIncrease) return;
    onQuantityChange?.(quantity + 1, product);
  };

  return (
    <article
      className={cn('w-full flex flex-col gap-3 p-3', showAddProduct && 'shadowCard', className)}
    >
      <div className="flex items-start gap-3">
        {showAddProduct && (
          <input
            type="checkbox"
            id={checkboxId}
            checked={checked}
            onChange={(e) => onCheckedChange?.(e.target.checked, product)}
            aria-label={`Pilih ${product.title}`}
            className="mt-!1 h-5! w-5! shrink-0 cursor-pointer rounded-[4px] border border-blue-cs-200 accent-text-blue-100"
          />
        )}

        <figure className="aspect-[1/1.15] w-[110px] shrink-0 overflow-hidden rounded-[12px]">
          {product.image_url && (
            <img
              src={product.image_url}
              alt={product.title}
              className="block h-full w-full object-cover"
            />
          )}
        </figure>

        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="body1 text-text-grey">{authorLabel(product)}</p>
          <label htmlFor={checkboxId} className="line-clamp-2 title3bold text-black cursor-pointer">
            {product.title}
          </label>
          <p className="body2 text-text-grey">{categoryLabel(product)}</p>
          <div className="mt-2 flex items-center justify-between gap-2">
            {showSubtotal && <p className="body2 text-text-grey">{quantity} Barang</p>}
            <p className="mt-2 title2bold text-black">
              Rp {formatPrice(Number(product.unit_price))}
            </p>
          </div>
        </div>
      </div>

      {!isLast && <div className="h-px w-full bg-blue-cs-200" />}
      {showAddProduct && (
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => onRemove?.(product)}
            aria-label={`Hapus ${product.title} dari keranjang`}
            className="flex items-center gap-2 body1medium text-text-danger-500 transition hover:opacity-80"
          >
            <Trash2 size={18} strokeWidth={1.8} />
            Hapus
          </button>

          <div className="flex items-center rounded-full border border-blue-cs-200 bg-white">
            <button
              type="button"
              onClick={handleDecrease}
              disabled={!canDecrease}
              aria-label="Kurangi jumlah"
              className="flex h-9 w-9 items-center justify-center text-black transition hover:bg-[#F3F8FF] disabled:opacity-40 disabled:hover:bg-transparent rounded-l-full"
            >
              <Minus size={16} strokeWidth={2} />
            </button>

            <span aria-live="polite" className="min-w-[32px] text-center body1medium text-black">
              {quantity}
            </span>

            <button
              type="button"
              onClick={handleIncrease}
              disabled={!canIncrease}
              aria-label="Tambah jumlah"
              className="flex h-9 w-9 items-center justify-center text-black transition hover:bg-[#F3F8FF] disabled:opacity-40 disabled:hover:bg-transparent rounded-r-full"
            >
              <Plus size={16} strokeWidth={2} />
            </button>
          </div>
        </div>
      )}
    </article>
  );
};

export default CardCartItem;
